import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Heart } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useWishlist } from "@/contexts/ShopContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHIPS = [
  { label: "All", value: "All" },
  { label: "The Wardrobe", value: "Apparel" },
  { label: "The Finishing Touch", value: "Jewellery" },
  { label: "The Carry Edit", value: "Handbag" },
  { label: "The Grounded Edit", value: "Footwear" },
];

const fallback = [
  {
    id: "DG001",
    name: "Midnight Denim Jacket",
    category: "Apparel",
    price: 2480,
    image:
      "https://images.pexels.com/photos/3595231/pexels-photo-3595231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tagline: "Sculptural denim, hand-finished.",
  },
];

export default function FeaturedCollection({
  activeCategory = "All",
  setActiveCategory,
}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currency, formatAmount } = useCurrency();
  const { has: inWishlist, toggle: toggleWishlist } = useWishlist();

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (mounted) setProducts(res.data || []);
      })
      .catch(() => {
        if (mounted) setProducts(fallback);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const formatPrice = (p) => {
    const inr = p?.price_inr;
    const usd = p?.price_usd ?? p?.price; // legacy fallback if API returns old schema
    if (currency === "INR" && inr != null) return formatAmount(inr);
    if (usd != null) return formatAmount(usd);
    return "";
  };

  const filtered = useMemo(() => {
    if (activeCategory === "All") return products;
    return products.filter(
      (p) => (p.category || "").toLowerCase() === activeCategory.toLowerCase()
    );
  }, [products, activeCategory]);

  // Asymmetric pattern, applied positionally to current filtered set
  const cardClass = (i, total) => {
    if (total === 1) return "md:col-span-12 aspect-[16/9]";
    if (total === 2)
      return i === 0
        ? "md:col-span-7 aspect-[4/5]"
        : "md:col-span-5 aspect-[3/4] md:mt-16";
    if (total <= 4) {
      const pat = [
        "md:col-span-7 aspect-[4/5]",
        "md:col-span-5 aspect-[3/4] md:mt-16",
        "md:col-span-5 aspect-[3/4]",
        "md:col-span-7 aspect-[4/5] md:mt-12",
      ];
      return pat[i] || "md:col-span-6 aspect-[3/4]";
    }
    const pat = [
      "md:col-span-7 aspect-[4/5]",
      "md:col-span-5 aspect-[3/4] md:mt-16",
      "md:col-span-4 aspect-[3/4]",
      "md:col-span-4 aspect-[3/4] md:mt-12",
      "md:col-span-4 aspect-[3/4]",
      "md:col-span-5 aspect-[3/4]",
      "md:col-span-7 aspect-[4/5] md:mt-16",
    ];
    return pat[i] || "md:col-span-6 aspect-[3/4]";
  };

  return (
    <section
      id="collections"
      data-testid="featured-collection"
      className="relative bg-midnight py-24 md:py-36 px-6 md:px-12 lg:px-24"
    >
      {/* Section header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
        <div className="md:col-span-3">
          <div className="eyebrow mb-4">Featured</div>
          <div className="text-silver text-xs tracking-[0.3em] uppercase">
            Seven Pieces
          </div>
        </div>
        <div className="md:col-span-9">
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            The <span className="italic text-silver">DenimGlow</span> Edit
            <br />
            <span className="text-white/45">Seven objects of quiet defiance.</span>
          </motion.h2>
        </div>
      </div>

      {/* Filter chips */}
      <div
        data-testid="collection-filters"
        className="flex flex-wrap items-center gap-x-2 gap-y-3 mb-12 md:mb-16 border-y border-white/8 py-6"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase text-silver/60 mr-4">
          Filter
        </span>
        {CHIPS.map((c) => {
          const active = activeCategory === c.value;
          return (
            <button
              key={c.value}
              type="button"
              data-testid={`filter-${c.value.toLowerCase()}`}
              onClick={() => setActiveCategory && setActiveCategory(c.value)}
              className={`relative px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase transition-all duration-500 border ${
                active
                  ? "border-silver text-white bg-white/5"
                  : "border-white/15 text-white/70 hover:border-white/50 hover:text-white"
              }`}
            >
              {c.label}
              {active && (
                <span className="absolute -bottom-px left-0 right-0 h-px bg-silver" />
              )}
            </button>
          );
        })}
        <div className="ml-auto text-[10px] tracking-[0.32em] uppercase text-white/45">
          {loading ? "—" : `${filtered.length} piece${filtered.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Asymmetric product grid */}
      {loading ? (
        <div
          data-testid="products-loading"
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {[...Array(7)].map((_, i) => (
            <div
              key={i}
              className={`bg-midnight-panel h-[460px] animate-pulse ${
                i % 3 === 0 ? "md:col-span-7" : "md:col-span-5"
              }`}
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="empty-state"
          className="min-h-[300px] flex flex-col items-center justify-center text-center border border-white/8 py-20"
        >
          <div className="eyebrow mb-4 text-silver/70">Coming soon</div>
          <h3 className="font-serif text-white text-3xl md:text-4xl mb-3">
            New pieces, in the making.
          </h3>
          <p className="text-white/55 max-w-md font-light">
            This edit is currently in the atelier. Join the inner circle to be
            notified when the first piece releases.
          </p>
          <button
            type="button"
            onClick={() => setActiveCategory && setActiveCategory("All")}
            data-testid="empty-reset"
            className="mt-8 inline-flex items-center gap-3 border border-white/30 text-white px-7 py-3 uppercase tracking-[0.32em] text-[11px] hover:border-white hover:bg-white/5 transition-all"
          >
            View all pieces
          </button>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8"
          >
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`product-card group relative block overflow-hidden bg-midnight-panel ${cardClass(
                  i,
                  filtered.length
                )}`}
              >
                <Link to={`/product/${p.id}`} className="absolute inset-0 z-10" aria-label={p.name} />
                <div className="absolute inset-0 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="product-img absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b10] via-transparent to-transparent opacity-90" />
                </div>

                {/* Top meta */}
                <div className="absolute top-5 left-5 right-5 flex items-start justify-between text-[10px] tracking-[0.3em] uppercase z-20">
                  {p.low_stock ? (
                    <span data-testid={`badge-low-stock-${p.id}`} className="text-silver bg-midnight-deep/60 backdrop-blur px-2 py-1 border border-silver/30">
                      Only {p.stock_remaining} left
                    </span>
                  ) : <span />}
                  <button
                    type="button"
                    aria-label="Wishlist"
                    data-testid={`card-wishlist-${p.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(p.id); }}
                    className={`relative z-20 w-9 h-9 flex items-center justify-center bg-midnight-deep/60 backdrop-blur border ${
                      inWishlist(p.id) ? "border-silver text-silver" : "border-white/15 text-white/70 hover:text-white"
                    }`}
                  >
                    <Heart size={13} strokeWidth={1.3} fill={inWishlist(p.id) ? "currentColor" : "transparent"} />
                  </button>
                </div>

                {/* Bottom info */}
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 z-20 pointer-events-none">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-white text-2xl md:text-3xl leading-tight">
                        {p.name}
                      </h3>
                      <p className="mt-2 text-white/55 text-xs md:text-sm font-light max-w-[28ch]">
                        {p.tagline}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-silver text-sm tracking-[0.2em]">
                        {formatPrice(p)}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase text-white/80 group-hover:text-white">
                        View <ArrowUpRight size={12} strokeWidth={1.3} />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Footnote */}
      <div className="mt-20 md:mt-28 flex flex-col md:flex-row md:items-end justify-between gap-8 border-t border-white/8 pt-10">
        <p className="text-white/55 max-w-lg text-sm md:text-base font-light leading-relaxed">
          Each DenimGlow piece is produced in editions of fewer than 50.
          Numbered, signed, and accompanied by a hand-stitched certificate.
        </p>
        <a
          href="#story"
          data-testid="collection-cta"
          className="inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-white border-b border-white/30 pb-2 hover:border-white transition-colors self-start md:self-end"
        >
          Discover the house
          <ArrowUpRight size={14} strokeWidth={1.3} />
        </a>
      </div>
    </section>
  );
}
